import React, { useState, useEffect } from "react";
import { Camera, Lock, Unlock, ArrowLeft, Edit3, Move } from "lucide-react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import PresetEditModal from "../components/PresetEditModal";
import PresetReframeModal from "../components/PresetReframeModal";

/* ── Zone definitions ───────────────────────────────────────────────── */
const ZONE_INFO = [
    { code: "GP",  label: "Gondola Bays",        checks: "Stock levels, facings, price tickets, shelf presentation",       types: ["gondola_bay", "gondola_shelving", "gondola"] },
    { code: "N",   label: "Fridge Doors",         checks: "Stock levels, equipment status, door seals, temperature",        types: ["fridge_door", "fridge_wall", "refrigerated_drinks"] },
    { code: "FZ",  label: "Freezer Doors",        checks: "Stock levels, equipment status, frost/ice, temperature",         types: ["freezer_door", "freezer", "ice_cream_freezer"] },
    { code: "PF",  label: "Pie Face Cabinet",     checks: "Empty trays, stock spreading, cabinet on, price labels",         types: ["pie_cabinet", "pie_face_cabinet"] },
    { code: "CF",  label: "Coffee Station",        checks: "Cups, lids, sugar, machine status, cleanliness",                types: ["coffee_station"] },
    { code: "POS", label: "POS Counter",           checks: "Counter tidiness, impulse displays, staff uniform",             types: ["counter_register", "pos_counter"] },
    { code: "AW",  label: "Auto Wall",             checks: "Stock levels, hook counts, presentation",                       types: ["auto_wall"] },
    { code: "IM",  label: "Impulse Fridge",        checks: "Stock levels, equipment status, temperature",                   types: ["impulse_fridge", "standalone_fridge"] },
    { code: "EC",  label: "Endcaps",               checks: "Stock fill, signage, promotional compliance",                   types: ["endcap"] },
    { code: "SD",  label: "Standalone Displays",   checks: "Fill level, condition, price tickets, stability",               types: ["standalone_display", "magazine_rack"] },
    { code: "DN",  label: "Dining / Seating",      checks: "Tables clean, chairs tidy, floor clean, condiments",            types: ["dining_seating"] },
    { code: "DT",  label: "Drive Thru",            checks: "Screens on & current, lane clear, signage intact",              types: ["drive_thru"] },
    { code: "FS",  label: "Floor Stacks",          checks: "Merchandised, ticketed, POS material, stable",                  types: ["floor_stacks"] },
    { code: "EN",  label: "Entry / Exit",          checks: "Customer flow, door cleanliness, signage",                      types: ["entry_exit"] },
    { code: "FC",  label: "Forecourt",             checks: "Surface, pumps, grass/weeds, bins, infrastructure",             types: ["forecourt"] },
    { code: "OV",  label: "Store Overview",         checks: "Overall appearance, cleanliness, stock on floor",              types: ["store_overview"] },
    { code: "CW",  label: "Confectionery",          checks: "Stock levels, shelf presentation",                            types: ["confectionery_wall", "confectionery_display", "counter_display"] },
    { code: "CC",  label: "Counter Confectionery",  checks: "Counter confectionery stock levels",                           types: ["counter_confectionery"] },
    { code: "MC",  label: "Macaron Cabinet",         checks: "Cabinet stock levels",                                        types: ["macaron_cabinet"] },
];

// Build reverse lookup: fixtureType → zone index
const fixtureTypeToZone = {};
ZONE_INFO.forEach((z, i) => z.types.forEach((t) => { fixtureTypeToZone[t] = i; }));

function groupPresetsByZone(presets) {
    const groups = {}; // zoneIndex → [preset, ...]
    const ungrouped = [];
    for (const p of presets) {
        const zi = fixtureTypeToZone[p.fixtureType];
        if (zi !== undefined) {
            (groups[zi] = groups[zi] || []).push(p);
        } else {
            ungrouped.push(p);
        }
    }
    // Return ALL zones in defined order — empty zones show as empty rows
    const result = [];
    ZONE_INFO.forEach((z, i) => {
        result.push({ zone: z, presets: groups[i] || [] });
    });
    // Append any ungrouped presets as a catch-all zone
    if (ungrouped.length > 0) {
        result.push({
            zone: { code: "??", label: "Other", checks: "Uncategorised presets", types: [] },
            presets: ungrouped,
        });
    }
    return result;
}

/* ── Shared components ──────────────────────────────────────────────── */

function ImageModal({ src, alt, onClose }) {
    useEffect(() => {
        const handleKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [onClose]);

    return (
        <div className="image-modal-overlay" onClick={onClose}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="image-modal-close" onClick={onClose}>ESC</button>
                <img className="image-modal-img" src={src} alt={alt} />
            </div>
        </div>
    );
}

function CropOverlay({ cropRegion }) {
    if (!cropRegion) return null;
    const { x, y, w, h } = cropRegion;
    return (
        <div className="crop-overlay">
            <div className="crop-dim" style={{ top: 0, left: 0, right: 0, height: `${y * 100}%` }} />
            <div className="crop-dim" style={{ top: `${(y + h) * 100}%`, left: 0, right: 0, bottom: 0 }} />
            <div className="crop-dim" style={{ top: `${y * 100}%`, left: 0, width: `${x * 100}%`, height: `${h * 100}%` }} />
            <div className="crop-dim" style={{ top: `${y * 100}%`, right: 0, left: `${(x + w) * 100}%`, height: `${h * 100}%` }} />
            <div className="crop-border" style={{
                top: `${y * 100}%`, left: `${x * 100}%`,
                width: `${w * 100}%`, height: `${h * 100}%`
            }} />
        </div>
    );
}

/* PresetCard kept for edit/reframe modals that reference it */
function PresetCard({ preset, siteId, onImageClick, onEdit, onReframe }) {
    const checkTags = (preset.checkTypes || "").split(",").filter(Boolean);

    return (
        <div className="preset-card">
            <div className="preset-card-image-wrap">
                {preset.referenceImageUrl ? (
                    <>
                        <img
                            className="preset-card-image"
                            src={preset.referenceImageUrl}
                            alt={preset.presetName}
                            onClick={() => onImageClick(preset.referenceImageUrl, preset.presetName)}
                        />
                        <CropOverlay cropRegion={preset.cropRegion} />
                    </>
                ) : (
                    <div className="preset-card-placeholder">
                        <Camera size={32} />
                        <span>{preset.locked ? "No reference image" : "Draft — needs framing"}</span>
                    </div>
                )}
            </div>
            <div className="preset-card-body">
                <div className="preset-card-header">
                    <span className="preset-card-name">{preset.presetName}</span>
                    {preset.cameraType === "NVR" && preset.camNum != null && (
                        <span className="preset-tag" style={{ background: "#dbeafe", color: "#1d4ed8", fontSize: 11 }}>NVR Ch{preset.camNum}</span>
                    )}
                    {preset.cameraType === "PTZ" && (
                        <span className="preset-tag" style={{ background: "#fef3c7", color: "#92400e", fontSize: 11 }}>PTZ</span>
                    )}
                    {preset.version > 1 && (
                        <span className="severity info">v{preset.version}</span>
                    )}
                </div>
                <div className="preset-card-detail">
                    <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                        {preset.fixtureType ? preset.fixtureType.replace(/_/g, " ") : "Unknown"}
                    </span>
                </div>
                {preset.fixtureId && (
                    <div className="preset-card-detail" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {preset.fixtureId}
                    </div>
                )}
                {checkTags.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                        {checkTags.map((tag) => (
                            <span key={tag} className="preset-tag">{tag}</span>
                        ))}
                    </div>
                )}
                <div className="preset-card-lock">
                    {preset.locked ? (
                        <>
                            <Lock size={12} />
                            <span>Locked by {preset.lockedBy || "system"}</span>
                        </>
                    ) : (
                        <>
                            <Unlock size={12} />
                            <span style={{ color: "var(--yellow)" }}>Unlocked</span>
                        </>
                    )}
                </div>
                <div className="preset-card-actions">
                    <button className="preset-action-btn" onClick={onEdit} title="Edit fixture type, description, crop">
                        <Edit3 size={13} /> Edit
                    </button>
                    <button className="preset-action-btn" onClick={onReframe} title="Re-frame camera position">
                        <Move size={13} /> Re-frame
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Zone row component ─────────────────────────────────────────────── */
function ZoneRow({ zone, presets, onImageClick, onEdit, onReframe, onToggleLock, lockingPreset }) {
    const empty = presets.length === 0;
    return (
        <div className={`zone-row${empty ? " zone-row-empty" : ""}`}>
            <div className="zone-row-header">
                <span className={`zone-code-badge${empty ? " zone-code-badge-empty" : ""}`}>{zone.code}</span>
                <span className="zone-row-label">{zone.label}</span>
                <span className="zone-row-count">
                    {empty ? <span style={{ color: "var(--text-muted)" }}>No presets</span> : `${presets.length} preset${presets.length !== 1 ? "s" : ""}`}
                </span>
            </div>
            <div className="zone-row-checks">{zone.checks}</div>
            {presets.length > 0 && (
                <div className="zone-row-presets">
                    {presets.map((p) => (
                        <div className="zone-preset-thumb" key={p.id}>
                            <div className="zone-preset-image-wrap">
                                {p.referenceImageUrl ? (
                                    <>
                                        <img
                                            src={p.referenceImageUrl}
                                            alt={p.presetName}
                                            onClick={() => onImageClick(p.referenceImageUrl, p.presetName)}
                                        />
                                        <CropOverlay cropRegion={p.cropRegion} />
                                    </>
                                ) : (
                                    <div className="zone-preset-placeholder">
                                        <Camera size={20} />
                                        {!p.locked && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1, opacity: 0.6 }}>DRAFT</span>}
                                    </div>
                                )}
                                <div className="zone-preset-actions">
                                    <button className="zone-preset-action-btn" onClick={() => onEdit(p)} title="Edit">
                                        <Edit3 size={12} />
                                    </button>
                                    <button className="zone-preset-action-btn" onClick={() => onReframe(p)} title="Re-frame">
                                        <Move size={12} />
                                    </button>
                                    <button
                                        className={`zone-preset-action-btn${p.locked ? " zone-preset-locked" : ""}`}
                                        onClick={() => onToggleLock(p)}
                                        title={p.locked ? "Unlock preset" : "Lock preset (captures image)"}
                                        disabled={lockingPreset === p.presetName}
                                    >
                                        {lockingPreset === p.presetName ? (
                                            <span className="lock-spinner" />
                                        ) : p.locked ? (
                                            <Lock size={12} />
                                        ) : (
                                            <Unlock size={12} />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="zone-preset-label">
                                <span className="zone-preset-name">{p.presetName}</span>
                                {p.fixtureId && <span className="zone-preset-fixture">{p.fixtureId}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Presets() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    const [selectedSiteId, setSelectedSiteId] = useState(null);
    const [storeDetail, setStoreDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const [modalImage, setModalImage] = useState(null);
    const [editPreset, setEditPreset] = useState(null);
    const [reframePreset, setReframePreset] = useState(null);

    const [lockingPreset, setLockingPreset] = useState(null);

    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState("asc");

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    useEffect(() => {
        api.presetStores()
            .then((data) => {
                setStores(data);
                setError(null);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const loadStoreDetail = (siteId) => {
        setSelectedSiteId(siteId);
        setDetailLoading(true);
        setDetailError(null);
        api.presetsByStore(siteId)
            .then((data) => {
                setStoreDetail(data);
                setDetailError(null);
            })
            .catch((err) => setDetailError(err.message))
            .finally(() => setDetailLoading(false));
    };

    const goBack = () => {
        setSelectedSiteId(null);
        setStoreDetail(null);
        setDetailError(null);
    };

    const handleEditSave = async (data) => {
        await api.updatePreset(selectedSiteId, editPreset.presetName, data);
        setEditPreset(null);
        loadStoreDetail(selectedSiteId); // refresh
    };

    const handleNudge = async (delta) => {
        return await api.nudgePreset(selectedSiteId, reframePreset.presetName, delta);
    };

    const handleToggleLock = async (preset) => {
        if (lockingPreset) return;
        setLockingPreset(preset.presetName);
        try {
            await api.togglePresetLock(selectedSiteId, preset.presetName);
            loadStoreDetail(selectedSiteId);
        } catch (err) {
            alert(`Lock toggle failed: ${err.message}`);
        } finally {
            setLockingPreset(null);
        }
    };

    const handleClearUnlocked = async () => {
        const unlocked = (storeDetail?.presets || []).filter(p => !p.locked);
        if (unlocked.length === 0) { alert("No unlocked presets to clear."); return; }
        if (!window.confirm(`Delete ${unlocked.length} unlocked preset(s)? Locked presets will not be affected.`)) return;
        try {
            const result = await api.clearUnlockedPresets(selectedSiteId);
            alert(`Cleared ${result.deleted} unlocked preset(s).`);
            loadStoreDetail(selectedSiteId);
        } catch (err) {
            alert(`Failed to clear presets: ${err.message}`);
        }
    };

    const handleConfirm = async (ptz) => {
        await api.confirmPreset(selectedSiteId, reframePreset.presetName, ptz);
        setReframePreset(null);
        loadStoreDetail(selectedSiteId); // refresh
    };

    // --- Store detail view ---
    if (selectedSiteId) {
        if (detailLoading) return <LoadingSpinner message="Loading presets..." />;

        if (detailError) {
            return (
                <div>
                    <button className="preset-back-btn" onClick={goBack}>
                        <ArrowLeft size={16} /> Back to stores
                    </button>
                    <div className="empty-state">
                        <h3>Failed to load presets</h3>
                        <p>{detailError}</p>
                        <button className="retry-btn" onClick={() => loadStoreDetail(selectedSiteId)}>Retry</button>
                    </div>
                </div>
            );
        }

        const storeName = storeDetail?.store?.name || `Store ${selectedSiteId}`;
        const presets = storeDetail?.presets || [];

        // Split by camera type
        const ptzPresets = presets.filter(p => p.cameraType !== "NVR");
        const nvrPresets = presets.filter(p => p.cameraType === "NVR");
        const ptzGroups = groupPresetsByZone(ptzPresets);
        const nvrGroups = groupPresetsByZone(nvrPresets);

        const renderZoneRows = (groups) => groups.map((g) => (
            <ZoneRow
                key={g.zone.code}
                zone={g.zone}
                presets={g.presets}
                onImageClick={(src, alt) => setModalImage({ src, alt })}
                onEdit={(p) => setEditPreset(p)}
                onReframe={(p) => setReframePreset(p)}
                onToggleLock={handleToggleLock}
                lockingPreset={lockingPreset}
            />
        ));

        return (
            <div>
                <button className="preset-back-btn" onClick={goBack}>
                    <ArrowLeft size={16} /> Back to stores
                </button>
                <div className="page-header">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                            <h2>{storeName}</h2>
                            <p>{presets.length} preset{presets.length !== 1 ? "s" : ""} ({ptzPresets.length} PTZ, {nvrPresets.length} NVR) {"\u2014"} Site {selectedSiteId}</p>
                        </div>
                        {presets.some(p => !p.locked) && (
                            <button
                                className="preset-back-btn"
                                style={{ color: "var(--red, #ef4444)", borderColor: "var(--red, #ef4444)", whiteSpace: "nowrap" }}
                                onClick={handleClearUnlocked}
                            >
                                Clear {presets.filter(p => !p.locked).length} Unlocked
                            </button>
                        )}
                    </div>
                </div>
                {presets.length > 0 ? (
                    <div className="zone-rows">
                        {ptzPresets.length > 0 && (
                            <>
                                <div style={{ padding: "12px 16px", background: "var(--bg-hover)", borderRadius: 8, fontWeight: 700, fontSize: 15, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <Camera size={18} /> PTZ Camera Presets ({ptzPresets.length})
                                </div>
                                {renderZoneRows(ptzGroups)}
                            </>
                        )}
                        {nvrPresets.length > 0 && (
                            <>
                                <div style={{ padding: "12px 16px", background: "var(--bg-hover)", borderRadius: 8, fontWeight: 700, fontSize: 15, marginTop: 16, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                                    <Camera size={18} /> NVR Fixed Camera Presets ({nvrPresets.length})
                                </div>
                                {renderZoneRows(nvrGroups)}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="empty-state">
                        <h3>No presets found</h3>
                        <p>This store has no active preset configurations.</p>
                    </div>
                )}
                {modalImage && (
                    <ImageModal
                        src={modalImage.src}
                        alt={modalImage.alt}
                        onClose={() => setModalImage(null)}
                    />
                )}
                {editPreset && (
                    <PresetEditModal
                        preset={editPreset}
                        siteId={selectedSiteId}
                        onSave={handleEditSave}
                        onClose={() => setEditPreset(null)}
                    />
                )}
                {reframePreset && (
                    <PresetReframeModal
                        preset={reframePreset}
                        siteId={selectedSiteId}
                        onNudge={handleNudge}
                        onConfirm={handleConfirm}
                        onClose={() => setReframePreset(null)}
                    />
                )}
            </div>
        );
    }

    // --- Store list view ---
    if (loading) return <LoadingSpinner message="Loading preset stores..." />;

    if (error) {
        return (
            <div className="empty-state">
                <h3>Failed to load stores</h3>
                <p>{error}</p>
                <button className="retry-btn" onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    const filtered = stores.filter((s) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (s.name || "").toLowerCase().includes(q) ||
            String(s.siteId).includes(q) ||
            (s.state || "").toLowerCase().includes(q)
        );
    });

    const sorted = sortField
        ? [...filtered].sort((a, b) => {
            const mult = sortDir === "desc" ? -1 : 1;
            let va = a[sortField], vb = b[sortField];
            if (sortField === "locked") {
                va = a.lockedCount === a.presetCount ? 1 : 0;
                vb = b.lockedCount === b.presetCount ? 1 : 0;
            }
            if (va == null) va = "";
            if (vb == null) vb = "";
            if (typeof va === "number" && typeof vb === "number") return (va - vb) * mult;
            return String(va).localeCompare(String(vb)) * mult;
        })
        : filtered;

    const SortTh = ({ label, field, style }) => {
        const active = sortField === field;
        return (
            <th
                style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}
                onClick={() => handleSort(field)}
            >
                {label} <span style={{ opacity: active ? 1 : 0.3, fontSize: 10 }}>{active && sortDir === "desc" ? "\u25BC" : "\u25B2"}</span>
            </th>
        );
    };

    return (
        <div>
            <div className="page-header">
                <h2>Presets</h2>
                <p>{stores.length} store{stores.length !== 1 ? "s" : ""} with configured presets</p>
            </div>

            <div className="filter-row" style={{ marginBottom: 16 }}>
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Search by name, site ID, or state..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ minWidth: 260 }}
                />
            </div>

            <div className="card">
                {sorted.length > 0 ? (
                    <table className="store-table">
                        <thead>
                            <tr>
                                <SortTh label="Site ID" field="siteId" />
                                <SortTh label="Name" field="name" />
                                <SortTh label="State" field="state" />
                                <SortTh label="Type" field="storeType" />
                                <SortTh label="Presets" field="presetCount" />
                                <SortTh label="Locked" field="locked" />
                                <SortTh label="Last Updated" field="lastUpdated" />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((s) => (
                                <tr
                                    key={s.siteId}
                                    onClick={() => loadStoreDetail(s.siteId)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <td style={{ color: "var(--text-muted)", fontSize: 13 }}>{s.siteId}</td>
                                    <td><span className="store-name">{s.name}</span></td>
                                    <td>{s.state}</td>
                                    <td style={{ fontSize: 13 }}>{s.storeType}</td>
                                    <td>{s.presetCount}</td>
                                    <td>
                                        {s.lockedCount === s.presetCount ? (
                                            <span style={{ color: "var(--green)" }}>{s.lockedCount}/{s.presetCount}</span>
                                        ) : (
                                            <span style={{ color: "var(--yellow)" }}>{s.lockedCount}/{s.presetCount}</span>
                                        )}
                                    </td>
                                    <td style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                                        {s.lastUpdated ? new Date(s.lastUpdated).toLocaleDateString() : "\u2014"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="empty-state">
                        <h3>No stores found</h3>
                        <p>Try adjusting your search criteria.</p>
                    </div>
                )}
            </div>

            <div style={{ textAlign: "center", padding: 16, color: "var(--text-muted)", fontSize: 13 }}>
                Showing {sorted.length} of {stores.length} stores
            </div>
        </div>
    );
}

export default Presets;
